import { useState, type ReactNode } from 'react';

import { onLinkClick } from '../app/router.js';
import { useT } from '../i18n/index.js';
import { AccountMenu } from './AccountMenu.js';
import { Brand, LangSwitch, ThemeToggle } from './TopBar.js';

/** The home-style top bar and a centred column, for the account and class pages. */
export function PageShell({
  children,
  wide = false,
  current,
}: {
  children: ReactNode;
  wide?: boolean;
  current?: 'home' | 'classes';
}) {
  const t = useT();
  return (
    <div className="home">
      <header className="topbar home-topbar">
        <Brand />
        <div className="topbar-spacer" />
        <nav className="home-nav">
          <a href="/" onClick={onLinkClick} aria-current={current === 'home' ? 'page' : undefined}>
            {t.nav.exercises}
          </a>
          <a
            href="/classes"
            onClick={onLinkClick}
            aria-current={current === 'classes' ? 'page' : undefined}
          >
            {t.menu.classes}
          </a>
        </nav>
        <AccountMenu />
        <LangSwitch />
        <ThemeToggle />
      </header>
      <main className={`page-main${wide ? ' page-wide' : ''}`}>{children}</main>
    </div>
  );
}

/** For pages that need an account: a card with Log in / Sign up that come back here. */
export function LoginRequired({ text }: { text?: string }) {
  const t = useT();
  const next = encodeURIComponent(window.location.pathname);
  return (
    <PageShell>
      <div className="card auth-card">
        <p className="auth-lead">{text ?? t.auth.signupText}</p>
        <div className="button-row">
          <a href={`/login?next=${next}`} onClick={onLinkClick} className="btn btn-primary btn-lg">
            {t.menu.login}
          </a>
          <a href={`/signup?next=${next}`} onClick={onLinkClick} className="btn btn-lg">
            {t.menu.signup}
          </a>
        </div>
      </div>
    </PageShell>
  );
}

/** A labelled input with an optional hint; the label wraps the control. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function PasswordInput({
  value,
  onChange,
  autoComplete,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  label: string;
}) {
  const t = useT();
  const [shown, setShown] = useState(false);
  return (
    <span className="password-input">
      <input
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        aria-label={label}
        required
      />
      <button type="button" className="link-btn" onClick={() => setShown((s) => !s)}>
        {shown ? t.auth.hide : t.auth.show}
      </button>
    </span>
  );
}

export function FormError({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p className="form-error" role="alert">
      {text}
    </p>
  );
}
